import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { baseurl } from "../config/url";

const ROLE_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "AccountAdmin", label: "Account Admin" },
  { value: "SalesAdmin", label: "Sales Admin" },
  { value: "technicalAdmin", label: "Technical Admin" },
  { value: "AnalysisAdmin", label: "Analysis Admin" },
];

const safeText = (v) => (v === null || v === undefined || v === "" ? "-" : String(v));
const safeLower = (v) => String(v || "").toLowerCase();

const ManageAdmins = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);

  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // UI state
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  // Details drawer/modal (read-only)
  const [selectedAdmin, setSelectedAdmin] = useState(null);

  // Create / Edit form
  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    role: "admin",
    password: "",
    id: null,
  });

  /** ✅ supports adminToken + token + auth.token + admin.token */
  const getToken = () => {
    try {
      const auth = JSON.parse(localStorage.getItem("auth") || "{}");
      const admin = JSON.parse(localStorage.getItem("admin") || "{}");

      return (
        localStorage.getItem("adminToken") ||
        auth?.token ||
        admin?.token ||
        localStorage.getItem("token") ||
        ""
      );
    } catch {
      return localStorage.getItem("adminToken") || localStorage.getItem("token") || "";
    }
  };

  const authConfig = useMemo(() => {
    const token = getToken();
    return {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetMessages = () => {
    setError("");
    setSuccess("");
  };

  const resetForm = () => {
    setForm({
      name: "",
      email: "",
      mobile: "",
      role: "admin",
      password: "",
      id: null,
    });
  };

  const fetchAdmins = async () => {
    const token = getToken();
    if (!token) {
      setError("Admin token not found. Please login again.");
      setAdmins([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      resetMessages();

      const response = await axios.get(`${baseurl}/api/admin/get-all-admin`, authConfig);

      const list = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.admins)
        ? response.data.admins
        : [];

      setAdmins(list);
    } catch (err) {
      console.error("Error fetching admins:", err.response?.data || err.message);
      setError(err.response?.data?.message || "Failed to load admins. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createAdmin = async () => {
    await axios.post(`${baseurl}/api/admin/create-admin`, form, authConfig);
  };

  const updateAdmin = async () => {
    await axios.put(
      `${baseurl}/api/admin/update-admin/${form.id}`,
      {
        name: form.name,
        email: form.email,
        mobile: form.mobile,
        role: form.role,
      },
      authConfig
    );
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    resetMessages();

    if (!form.name || !form.email || !form.mobile || !form.role) {
      setError("Name, Email, Mobile, Role are required.");
      return;
    }

    if (!form.id && !form.password) {
      setError("Password is required to create a new admin.");
      return;
    }

    try {
      setActionLoading(true);

      if (form.id) {
        await updateAdmin();
        setSuccess("Admin updated successfully!");
      } else {
        await createAdmin();
        setSuccess("Admin created successfully!");
      }

      resetForm();
      setShowCreate(false);
      await fetchAdmins();
    } catch (err) {
      console.error("Error saving admin:", err.response?.data || err.message);
      setError(err.response?.data?.message || "Failed to save admin. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  // ✅ click row -> open details (read-only)
  const openAdminDetails = (admin) => {
    setSelectedAdmin(admin);
  };

  const closeDetails = () => setSelectedAdmin(null);

  // ✅ Create button -> open create form modal
  const openCreate = () => {
    resetMessages();
    resetForm();
    setShowCreate(true);
  };

  // ✅ Edit inside table
  const handleEdit = (admin) => {
    resetMessages();
    setForm({
      name: admin?.name || "",
      email: admin?.email || "",
      mobile: admin?.mobile || "",
      role: admin?.role || "admin",
      id: admin?._id || null,
      password: "",
    });
    setSelectedAdmin(null);
    setShowCreate(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ✅ Delete inside table
  const handleDelete = async (id) => {
    const ok = window.confirm("Are you sure you want to delete this admin?");
    if (!ok) return;

    try {
      resetMessages();
      setActionLoading(true);

      await axios.delete(`${baseurl}/api/admin/delete-admin/${id}`, authConfig);

      setAdmins((prev) => prev.filter((a) => a._id !== id));
      setSuccess("Admin deleted successfully!");
      if (selectedAdmin?._id === id) setSelectedAdmin(null);
    } catch (err) {
      console.error("Error deleting admin:", err.response?.data || err.message);
      setError(err.response?.data?.message || "Failed to delete admin.");
    } finally {
      setActionLoading(false);
    }
  };

  // ✅ Search
  const filteredAdmins = useMemo(() => {
    const q = safeLower(search.trim());
    if (!q) return admins;
    return admins.filter((a) => {
      return (
        safeLower(a?._id).includes(q) ||
        safeLower(a?.name).includes(q) ||
        safeLower(a?.email).includes(q) ||
        safeLower(a?.mobile).includes(q) ||
        safeLower(a?.role).includes(q)
      );
    });
  }, [admins, search]);

  // ESC closes modals
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setShowCreate(false);
        setSelectedAdmin(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm px-6 py-5">
          <div className="text-lg font-semibold text-slate-800">Loading admins…</div>
          <div className="text-sm text-slate-500 mt-1">Please wait</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Manage Admins</h1>
            <p className="text-sm text-slate-500 mt-1">
              Create admins, view details, edit and delete from the table.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={fetchAdmins}
              disabled={actionLoading}
              className="px-4 py-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 disabled:opacity-50"
            >
              Refresh
            </button>

            {/* ✅ Create button top-right */}
            <button
              onClick={openCreate}
              disabled={actionLoading}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              + Create
            </button>
          </div>
        </div>

        {/* Alerts */}
        {(error || success) && (
          <div className="space-y-2">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3">
                {error}
              </div>
            )}
            {success && (
              <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl p-3">
                {success}
              </div>
            )}
          </div>
        )}

        {/* List */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-lg font-bold text-slate-900">Admins</div>
              <div className="text-sm text-slate-500 mt-1">
                Total: <b>{admins.length}</b> • Showing: <b>{filteredAdmins.length}</b>
              </div>
            </div>

            <div className="w-full sm:w-96">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by ID, name, email, mobile, role..."
                className="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
          </div>

          {filteredAdmins.length === 0 ? (
            <div className="p-6 text-slate-600">No admins found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-700">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Admin</th>
                    <th className="px-4 py-3 text-left font-semibold">Mobile</th>
                    <th className="px-4 py-3 text-left font-semibold">Role</th>
                    <th className="px-4 py-3 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAdmins.map((a) => (
                    <tr
                      key={a._id}
                      className="border-t hover:bg-slate-50 cursor-pointer"
                      onClick={() => openAdminDetails(a)}
                      title="Click to view details"
                    >
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-900">{safeText(a.name)}</div>
                        <div className="text-xs text-slate-500">{safeText(a.email)}</div>
                        <div className="text-xs text-slate-400 font-mono mt-1">
                          {safeText(a._id)}
                        </div>
                      </td>

                      <td className="px-4 py-3 text-slate-700">{safeText(a.mobile)}</td>

                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                          {safeText(a.role)}
                        </span>
                      </td>

                      {/* ✅ Actions inside table */}
                      <td
                        className="px-4 py-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleEdit(a)}
                            disabled={actionLoading}
                            className="px-3 py-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50"
                          >
                            Edit
                          </button>

                          <button
                            onClick={() => handleDelete(a._id)}
                            disabled={actionLoading}
                            className="px-3 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* <div className="p-4 border-t border-slate-200 text-xs text-slate-500">
            API used: <span className="font-mono">/api/admin/get-all-admin</span>,{" "}
            <span className="font-mono">/api/admin/create-admin</span>,{" "}
            <span className="font-mono">/api/admin/update-admin/:id</span>,{" "}
            <span className="font-mono">/api/admin/delete-admin/:id</span>
          </div> */}
        </div>
      </div>

      {/* ✅ Details Modal (READ ONLY, no edit/delete here) */}
      {selectedAdmin && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
          onClick={closeDetails}
        >
          <div
            className="bg-white w-full max-w-2xl rounded-2xl shadow-lg border border-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-lg font-bold text-slate-900">Admin Details</div>
                <div className="text-xs text-slate-500 mt-1 font-mono break-all">
                  {safeText(selectedAdmin._id)}
                </div>
              </div>
              <button
                className="px-3 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700"
                onClick={closeDetails}
              >
                ✕
              </button>
            </div>

            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <DetailCard label="Name" value={selectedAdmin.name} />
              <DetailCard label="Email" value={selectedAdmin.email} />
              <DetailCard label="Mobile" value={selectedAdmin.mobile} />
              <DetailCard label="Role" value={selectedAdmin.role} />
              <DetailCard
                label="Created At"
                value={
                  selectedAdmin.createdAt
                    ? new Date(selectedAdmin.createdAt).toLocaleString("en-IN")
                    : "-"
                }
              />
              <DetailCard
                label="Updated At"
                value={
                  selectedAdmin.updatedAt
                    ? new Date(selectedAdmin.updatedAt).toLocaleString("en-IN")
                    : "-"
                }
              />
            </div>

            <div className="p-5 border-t border-slate-200 flex justify-end">
              <button
                onClick={closeDetails}
                className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Create/Edit Form Modal */}
      {showCreate && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
          onClick={() => setShowCreate(false)}
        >
          <div
            className="bg-white w-full max-w-xl rounded-2xl shadow-lg border border-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-lg font-bold text-slate-900">
                  {form.id ? "Update Admin" : "Create Admin"}
                </div>
                <div className="text-sm text-slate-500 mt-1">
                  {form.id ? "Update admin details" : "Create a new admin account"}
                </div>
              </div>
              <button
                className="px-3 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700"
                onClick={() => setShowCreate(false)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleFormSubmit} className="p-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-600">Name</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="Full name"
                  required
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="name@email.com"
                  required
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600">Mobile</label>
                <input
                  value={form.mobile}
                  onChange={(e) => setForm((p) => ({ ...p, mobile: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="10-digit mobile"
                  required
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-600">Role</label>
                <select
                  value={form.role}
                  onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>

              {!form.id && (
                <div>
                  <label className="text-xs font-semibold text-slate-600">Password</label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    placeholder="Create a strong password"
                    required
                  />
                  <div className="text-xs text-slate-500 mt-1">
                    Password required only when creating.
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {actionLoading ? (form.id ? "Updating..." : "Creating...") : form.id ? "Update" : "Create"}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    resetMessages();
                    resetForm();
                    setShowCreate(false);
                  }}
                  disabled={actionLoading}
                  className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const DetailCard = ({ label, value }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
    <div className="text-xs text-slate-500">{label}</div>
    <div className="text-sm font-semibold text-slate-900 break-all">{safeText(value)}</div>
  </div>
);

export default ManageAdmins;




































// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import { baseurl } from "../config/url";

// const ManageAdmins = () => {
//   const [admins, setAdmins] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState("");
//   const [success, setSuccess] = useState("");
//   const [form, setForm] = useState({
//     name: "",
//     email: "",
//     mobile: "",
//     role: "admin",
//     password: "",
//     id: null,
//   });

//   // ✅ Read token from localStorage (supports many formats)
//   const getToken = () => {
//     try {
//       // common: { token, role } saved in auth
//       const auth = JSON.parse(localStorage.getItem("auth") || "{}");
//       return (
//         auth?.token ||
//         JSON.parse(localStorage.getItem("admin") || "{}")?.token || // if you store admin object
//         localStorage.getItem("token") || // fallback
//         ""
//       );
//     } catch {
//       return localStorage.getItem("token") || "";
//     }
//   };

//   // ✅ Axios config with Authorization header
//   const authConfig = () => {
//     const token = getToken();
//     return {
//       headers: {
//         Authorization: `Bearer ${token}`,
//       },
//       withCredentials: true,
//     };
//   };

//   const fetchAdmins = async () => {
//     const token = getToken();
//     if (!token) {
//       setError("Admin token not found. Please login again.");
//       setAdmins([]);
//       return;
//     }

//     try {
//       const response = await axios.get(
//         `${baseurl}/api/admin/get-all-admin`,
//         authConfig()
//       );

//       // ✅ handle both shapes:
//       // res.data = [...]  OR  res.data.admins = [...]
//       const list = Array.isArray(response.data)
//         ? response.data
//         : Array.isArray(response.data?.admins)
//         ? response.data.admins
//         : [];

//       setAdmins(list);
//       setError("");
//     } catch (error) {
//       console.error("Error fetching admins:", error.response?.data || error.message);
//       setError(
//         error.response?.data?.message ||
//           "Failed to load admins. Please try again."
//       );
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchAdmins();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   const handleFormSubmit = async (e) => {
//     e.preventDefault();

//     if (
//       !form.name ||
//       !form.email ||
//       !form.mobile ||
//       !form.role ||
//       (!form.id && !form.password)
//     ) {
//       setError("All fields are required.");
//       return;
//     }

//     try {
//       setError("");
//       setSuccess("");

//       if (form.id) {
//         await updateAdmin();
//         setSuccess("Admin updated successfully!");
//       } else {
//         await createAdmin();
//         setSuccess("Admin created successfully!");
//       }

//       setForm({
//         name: "",
//         email: "",
//         mobile: "",
//         role: "admin",
//         password: "",
//         id: null,
//       });

//       await fetchAdmins(); // ✅ refresh list
//     } catch (error) {
//       console.error("Error saving admin:", error.response?.data || error.message);
//       setError(error.response?.data?.message || "Failed to save admin. Please try again.");
//     }
//   };

//   const createAdmin = async () => {
//     const token = getToken();
//     if (!token) throw new Error("Token missing");

//     await axios.post(`${baseurl}/api/admin/create-admin`, form, authConfig());
//   };

//   const updateAdmin = async () => {
//     const token = getToken();
//     if (!token) throw new Error("Token missing");

//     await axios.put(
//       `${baseurl}/api/admin/update-admin/${form.id}`,
//       {
//         name: form.name,
//         email: form.email,
//         mobile: form.mobile,
//         role: form.role,
//       },
//       authConfig()
//     );
//   };

//   const handleEdit = (admin) => {
//     setSuccess("");
//     setError("");
//     setForm({
//       name: admin.name,
//       email: admin.email,
//       mobile: admin.mobile,
//       role: admin.role,
//       id: admin._id,
//       password: "",
//     });
//   };

//   const handleDelete = async (id) => {
//     if (!window.confirm("Are you sure you want to delete this admin?")) return;

//     try {
//       setError("");
//       setSuccess("");

//       const token = getToken();
//       if (!token) throw new Error("Token missing");

//       await axios.delete(
//         `${baseurl}/api/admin/delete-admin/${id}`,
//         authConfig()
//       );

//       setAdmins((prev) => prev.filter((admin) => admin._id !== id));
//       setSuccess("Admin deleted successfully!");
//     } catch (error) {
//       console.error("Error deleting admin:", error.response?.data || error.message);
//       setError(error.response?.data?.message || "Failed to delete admin.");
//     }
//   };

//   if (loading) {
//     return (
//       <div className="flex items-center justify-center h-screen bg-gray-100">
//         <p className="text-xl font-semibold text-blue-600 animate-pulse">
//           Loading Admins...
//         </p>
//       </div>
//     );
//   }

//   return (
//     <div className="min-h-screen bg-gradient-to-r from-gray-50 to-blue-50 py-10 px-4 lg:px-16">
//       <div className="relative flex flex-col">
//         <div className="flex-1 p-6 bg-white rounded-lg shadow-lg">
//           <h1 className="text-4xl font-extrabold text-center text-gray-800 mb-12">
//             Manage Admins
//           </h1>

//           {error && <p className="text-red-500 text-center mb-4">{error}</p>}
//           {success && (
//             <p className="text-green-500 text-center mb-4">{success}</p>
//           )}

//           {/* Form Section */}
//           <div className="bg-white rounded-lg shadow-md p-6 mb-6">
//             <form
//               onSubmit={handleFormSubmit}
//               className="flex flex-wrap gap-4 items-center"
//             >
//               <input
//                 type="text"
//                 placeholder="Name"
//                 value={form.name}
//                 onChange={(e) => setForm({ ...form, name: e.target.value })}
//                 className="p-3 border rounded-lg flex-1"
//                 required
//               />
//               <input
//                 type="email"
//                 placeholder="Email"
//                 value={form.email}
//                 onChange={(e) => setForm({ ...form, email: e.target.value })}
//                 className="p-3 border rounded-lg flex-1"
//                 required
//               />
//               <input
//                 type="text"
//                 placeholder="Mobile"
//                 value={form.mobile}
//                 onChange={(e) => setForm({ ...form, mobile: e.target.value })}
//                 className="p-3 border rounded-lg flex-1"
//                 required
//               />
//               <select
//                 value={form.role}
//                 onChange={(e) => setForm({ ...form, role: e.target.value })}
//                 className="p-3 border rounded-lg flex-1"
//               >
//                 <option value="admin">Admin</option>
//                 <option value="AccountAdmin">Account Admin</option>
//                 <option value="SalesAdmin">Sales Admin</option>
//                 <option value="technicalAdmin">Technical Admin</option>
//                 <option value="AnalysisAdmin">Analysis Admin</option>
//               </select>

//               {!form.id && (
//                 <input
//                   type="password"
//                   placeholder="Password"
//                   value={form.password}
//                   onChange={(e) =>
//                     setForm({ ...form, password: e.target.value })
//                   }
//                   className="p-3 border rounded-lg flex-1"
//                   required
//                 />
//               )}

//               <button
//                 type="submit"
//                 className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600"
//               >
//                 {form.id ? "Update Admin" : "Create Admin"}
//               </button>

//               {form.id && (
//                 <button
//                   type="button"
//                   onClick={() =>
//                     setForm({
//                       name: "",
//                       email: "",
//                       mobile: "",
//                       role: "admin",
//                       password: "",
//                       id: null,
//                     })
//                   }
//                   className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600"
//                 >
//                   Cancel
//                 </button>
//               )}
//             </form>
//           </div>

//           {/* Table Section */}
//           <div className="overflow-x-auto bg-white rounded-lg shadow-md">
//             {admins.length > 0 ? (
//               <table className="table-auto w-full text-sm border-collapse">
//                 <thead>
//                   <tr className="bg-gradient-to-r from-blue-400 to-blue-500 text-white">
//                     <th className="px-6 py-4 text-left font-bold">ID</th>
//                     <th className="px-6 py-4 text-left font-bold">Name</th>
//                     <th className="px-6 py-4 text-left font-bold">Email</th>
//                     <th className="px-6 py-4 text-left font-bold">Mobile</th>
//                     <th className="px-6 py-4 text-left font-bold">Role</th>
//                     <th className="px-6 py-4 text-center font-bold">Actions</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {admins.map((admin, index) => (
//                     <tr
//                       key={admin._id}
//                       className={`${
//                         index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
//                       } hover:bg-gray-200 transition-colors duration-300`}
//                     >
//                       <td className="px-6 py-4 text-gray-800 font-semibold">
//                         {admin._id}
//                       </td>
//                       <td className="px-6 py-4 text-gray-700">{admin.name}</td>
//                       <td className="px-6 py-4 text-gray-700">{admin.email}</td>
//                       <td className="px-6 py-4 text-gray-700">{admin.mobile}</td>
//                       <td className="px-6 py-4 text-gray-700">{admin.role}</td>
//                       <td className="px-6 py-4 text-center">
//                         <button
//                           onClick={() => handleEdit(admin)}
//                           className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 mr-2"
//                         >
//                           Edit
//                         </button>
//                         <button
//                           onClick={() => handleDelete(admin._id)}
//                           className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600"
//                         >
//                           Delete
//                         </button>
//                       </td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             ) : (
//               <p className="px-6 py-4 text-center text-gray-600 font-medium">
//                 No admins found.
//               </p>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ManageAdmins;